import {
  IEmployeeAppInboxMoment,
  IEmployeeAppInboxResponse,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { IUserDenormalized } from '@dadohr/shared/domainTypes/user';
import { compact, escapeRegExp, sortBy, uniqBy } from 'lodash';
import { matchText } from '../../../../../../../services/match';
import { toFullName } from '../../../../../../../services/user';
import { ROUTES_V2 } from '../../../../../../routes';
import { groupMessages, groupTasks } from '../../../pages/Inbox/groups';
import { MessageGroup, TaskGroup } from '../../../pages/Inbox/types';

type InboxSearchOptions = {
  // excludeTitle: boolean;
  // excludeContent: boolean;
  caseSensitive: boolean;
  filterByType: ('USER' | 'TASK' | 'MSG' | 'MOMENT')[]; // empty searches for all
  contextLength: number;
};

type Match = {
  match: {
    start: number;
    end: number;
    txt: string;
  };
  withContext: {
    start: number;
    end: number;
    txt: string;
  };
  input: string;
};

type InboxMatchCandidateUser = {
  type: 'USER';
  item: IUserDenormalized;
};

type InboxMatchCanidateTask = {
  type: 'TASK';
  item: TaskGroup;
};

type InboxMatchCandidateMessage = {
  type: 'MSG';
  item: MessageGroup;
};

type InboxMatchCandidateMoment = {
  type: 'MOMENT';
  item: IEmployeeAppInboxMoment;
};

export type InboxMatchedItem =
  | (InboxMatchCandidateUser & { matches: Match[] })
  | (InboxMatchCanidateTask & { matches: Match[] })
  | (InboxMatchCandidateMessage & { matches: Match[] })
  | (InboxMatchCandidateMoment & { matches: Match[] });

export type InboxMatchCandidate =
  | InboxMatchCandidateUser
  | InboxMatchCanidateTask
  | InboxMatchCandidateMessage
  | InboxMatchCandidateMoment;

export const inboxDataToMatchCandidates = (
  data: IEmployeeAppInboxResponse
): InboxMatchCandidate[] => {
  const taskGroups = groupTasks(data.tasks);
  const messageGroups = groupMessages(data.messages);
  const users = sortBy(
    uniqBy(
      [
        ...data.tasks.map((t) => t.participant),
        ...data.messages.map((m) => m.participant)
      ],
      (u) => u.publicId
    ),
    (u) => toFullName(u)
  );
  return [
    ...users.map((u) => ({ type: 'USER' as const, item: u })),
    ...taskGroups.map((t) => ({ type: 'TASK' as const, item: t })),
    ...messageGroups.map((m) => ({ type: 'MSG' as const, item: m })),
    ...data.moments.map((m) => ({ type: 'MOMENT' as const, item: m }))
  ];
};

const matchUser = (
  u: IUserDenormalized,
  query: RegExp,
  opts: InboxSearchOptions
): Match[] => {
  const text = toFullName(u);
  return matchText(text, query, opts.contextLength);
};

const matchTaskGroup = (
  g: TaskGroup,
  query: RegExp,
  opts: InboxSearchOptions
): Match[] => {
  // return task.tasks.filter((t) => t.name.match(query));
  const text =
    g.tasks.length === 1 ? g.tasks[0].taskTitleInterpolated : g.taskTitle;
  return matchText(text, query, opts.contextLength);
};

const matchMessageGroup = (
  g: MessageGroup,
  query: RegExp,
  opts: InboxSearchOptions
): Match[] => {
  const text =
    g.messages.length === 1 ? g.messages[0].msgSubjectInterpolated : g.msgTitle;
  return matchText(text, query, opts.contextLength);
};

const matchMoment = (
  m: IEmployeeAppInboxMoment,
  query: RegExp,
  opts: InboxSearchOptions
): Match[] => {
  const text = m.templateTitle;
  return matchText(text, query, opts.contextLength);
};

const withMatchssOrNull = (
  candidate: InboxMatchCandidate,
  matches: Match[]
): InboxMatchedItem | null =>
  matches.length ? { ...candidate, matches } : null;

export const searchInInbox = (
  candidates: InboxMatchCandidate[],
  query: string,
  searchOptions: Partial<InboxSearchOptions> = {}
): InboxMatchedItem[] => {
  const opts: InboxSearchOptions = {
    caseSensitive: searchOptions.caseSensitive ?? false,
    filterByType: searchOptions.filterByType ?? [],
    contextLength: searchOptions.contextLength ?? 40
  };

  const regexp = new RegExp(
    escapeRegExp(query),
    opts.caseSensitive ? 'g' : 'gi'
  );
  const getCandidatesByType = <T extends InboxMatchCandidate['type']>(
    candidates: InboxMatchCandidate[],
    type: T
  ): Extract<InboxMatchCandidate, { type: T }>[] => {
    const fbt = opts.filterByType;
    if (fbt.length && !fbt.includes(type)) {
      return [];
    }
    return candidates.filter((c) => c.type === type) as Extract<
      InboxMatchCandidate,
      { type: T }
    >[];
  };

  const users = getCandidatesByType(candidates, 'USER');
  const tasks = getCandidatesByType(candidates, 'TASK');
  const messages = getCandidatesByType(candidates, 'MSG');
  const moments = getCandidatesByType(candidates, 'MOMENT');

  return compact([
    ...users.map((u) => withMatchssOrNull(u, matchUser(u.item, regexp, opts))),
    ...tasks.map((t) =>
      withMatchssOrNull(t, matchTaskGroup(t.item, regexp, opts))
    ),
    ...messages.map((m) =>
      withMatchssOrNull(m, matchMessageGroup(m.item, regexp, opts))
    ),
    ...moments.map((m) =>
      withMatchssOrNull(m, matchMoment(m.item, regexp, opts))
    )
  ]);
};

export const inboxSearchItemToHref = (
  userAccessToken: string,
  d: InboxMatchedItem
) => {
  if (d.type === 'USER') {
    return ROUTES_V2.inbox.url(userAccessToken, {
      u: [d.item.publicId]
    });
  }
  if (d.type === 'TASK') {
    if (d.item.tasks.length === 1) {
      const firstTask = d.item.tasks[0];
      // go to single instance page for participants
      if (firstTask.actorKey === PARTICIPANT_ACTOR_KEY) {
        return ROUTES_V2.taskDetails.url(
          userAccessToken,
          firstTask.instanceAccessToken,
          firstTask.taskId
        );
      }
    }
    return ROUTES_V2.inboxTasks.url(userAccessToken, d.item.taskTemplateId);
  }
  if (d.type === 'MSG') {
    if (d.item.messages.length === 1) {
      const firstMessage = d.item.messages[0];
      // go to single instance page
      if (firstMessage.actorKey === PARTICIPANT_ACTOR_KEY) {
        return ROUTES_V2.messageDetails.url(
          userAccessToken,
          firstMessage.instanceAccessToken,
          firstMessage.msgTemplateId
        );
      }
    }
    return ROUTES_V2.inboxMessages.url(userAccessToken, d.item.msgTemplateId);
  }
  if (d.type === 'MOMENT') {
    // TODO moment within inbox
    return '';
  }
  return '';
};
